/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 * Shows success message after submission (even if email doesn't exist, for security).
 * Provides link back to login page.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Alert,
  Anchor,
  Stack,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { supabase } from '@/lib/supabase';

interface ForgotPasswordForm {
  email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ForgotPasswordForm>({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : 'Invalid email address',
    },
  });

  const handleSubmit = async (values: ForgotPasswordForm) => {
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        form.reset();
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Forgot Password</Title>
          <Text c="dimmed" size="sm" ta="center">
            Enter your email and we'll send you a link to reset your password
          </Text>

          <Paper withBorder p={30} mt={30} radius="md">
            {success ? (
              <Stack gap="md">
                <Alert
                  icon={<IconCircleCheck size={16} />}
                  title="Check your email"
                  color="teal"
                >
                  If this email exists in our system, a password reset link has
                  been sent.
                </Alert>

                <Text size="sm" c="dimmed" ta="center">
                  Please check your inbox and follow the instructions to reset
                  your password. The link will expire in 1 hour.
                </Text>

                <Button component={Link} to="/login" variant="outline" fullWidth>
                  Back to Login
                </Button>
              </Stack>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  {error && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      title="Error"
                      color="red"
                    >
                      {error}
                    </Alert>
                  )}

                  <TextInput
                    label="Email"
                    placeholder="you@example.com"
                    required
                    {...form.getInputProps('email')}
                  />

                  <Button type="submit" fullWidth loading={isLoading}>
                    Send Reset Link
                  </Button>

                  <Text size="sm" ta="center">
                    <Anchor component={Link} to="/login" size="sm">
                      Back to Login
                    </Anchor>
                  </Text>

                  <Text size="sm" ta="center" c="dimmed">
                    Don't have an account?{' '}
                    <Anchor component={Link} to="/register" size="sm">
                      Join the waitlist
                    </Anchor>
                  </Text>
                </Stack>
              </form>
            )}
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
